import axios from "axios";

const API_URL = process.env.VUE_APP_API_BASE_URL;
const USE_MOCK = process.env.VUE_APP_AGENCY_BILLING_MOCK === "true";

export const PLATFORM_PLAN_META = {
  standard: {
    name: "Standard",
    headline: "Everything your team needs to launch",
    recommended: false,
    features: [
      "Full producer portal for every seat",
      "Client accounts & proposals",
      "Gap analysis & enroll tools",
      "Email support",
    ],
  },
  pro: {
    name: "Pro",
    headline: "Grow faster with advanced team tools",
    recommended: true,
    features: [
      "Everything in Standard",
      "Advanced reporting & analytics",
      "Priority support",
      "Team collaboration tools",
    ],
  },
};

const mockStore = {
  selected_plan_code: "pro",
  platform_seat_limit: 10,
  platform_seats_used: 4,
  seat_limit_min: 1,
  seat_limit_max: 100,
  monthly_amount: 0,
  platform_subscription_editable: true,
};

const authHeader = () => ({
  Authorization: `Bearer ${window.__protegoGetAccessToken ? window.__protegoGetAccessToken() : null}`,
  Accept: "application/json",
  "Content-Type": "application/json",
});

function enrichPlans(plans = []) {
  return plans.map((plan) => ({
    ...(PLATFORM_PLAN_META[plan.code] || {}),
    ...plan,
  }));
}

function estimateMonthlyTotal(plan, { isAgencyBill, seats, agencyBaseMonthly = 0, billingInterval = "month" }) {
  if (!plan) {
    return null;
  }

  if (billingInterval === "year") {
    if (isAgencyBill) {
      const seatCount = Math.max(0, Number(seats) || 0);
      const baseAnnual = plan.agency_base_annual ?? 0;
      const perSeatAnnual = plan.price_per_seat_annual ?? plan.price_annual ?? 0;
      return Math.round(baseAnnual + perSeatAnnual * seatCount);
    }

    return Math.round(plan.total_price_annual ?? plan.price_annual ?? plan.price_monthly ?? 0);
  }

  if (isAgencyBill) {
    const seatCount = Math.max(0, Number(seats) || 0);
    const base = plan.agency_base_monthly ?? agencyBaseMonthly ?? 0;
    const perSeat = plan.price_per_seat ?? plan.price_monthly ?? 0;

    return Math.round(base + perSeat * seatCount);
  }

  return Math.round(plan.price_monthly || 0);
}

/** Per-seat (or solo) monthly rate shown on plan cards — never the full agency total. */
export function planDisplayPrice(plan, { isAgencyBill = false, billingInterval = "month" } = {}) {
  if (!plan) {
    return null;
  }

  if (billingInterval === "year") {
    if (isAgencyBill) {
      const annual = plan.price_per_seat_annual ?? plan.price_annual ?? 0;
      return annual > 0 ? Math.round(annual / 12) : Math.round(plan.price_per_seat ?? plan.price_monthly ?? 0);
    }

    const annual = plan.price_annual ?? plan.total_price_annual ?? 0;
    return annual > 0 ? Math.round(annual / 12) : Math.round(plan.price_monthly || 0);
  }

  if (isAgencyBill) {
    return Math.round(plan.price_per_seat ?? plan.price_monthly ?? 0);
  }

  return Math.round(plan.price_monthly || 0);
}

export function estimatePlanMonthlyEquivalent(plan, { isAgencyBill = false, seats, agencyBaseMonthly = 0, billingInterval = "month" } = {}) {
  const total = estimateMonthlyTotal(plan, {
    isAgencyBill,
    seats,
    agencyBaseMonthly,
    billingInterval,
  });

  if (total == null) {
    return null;
  }

  if (billingInterval === "year") {
    return Math.round(total / 12);
  }

  return total;
}

export function agencyBaseDisplayMonthly(plan, { agencyBaseMonthly = 0, billingInterval = "month" } = {}) {
  if (billingInterval === "year") {
    const annual = plan?.agency_base_annual;
    if (annual != null && annual > 0) {
      return Math.round(annual / 12);
    }
  }

  const monthly = plan?.agency_base_monthly ?? agencyBaseMonthly ?? 0;
  return Math.round(monthly);
}

export function billingPriceUnit({ isAgencyBill = false } = {}) {
  if (isAgencyBill) {
    return "/seat/mo";
  }

  return "/mo";
}

export function billingPriceNote({ billingInterval = "month" } = {}) {
  return billingInterval === "year" ? "billed annually" : "";
}

function syncMockMonthlyAmount(plans = []) {
  const plan = planByCode(mockStore.selected_plan_code, plans);
  mockStore.monthly_amount = estimateMonthlyTotal(plan, {
    isAgencyBill: true,
    seats: mockStore.platform_seat_limit,
  }) ?? 0;
}

export function createMockPlatformBilling(overrides = {}) {
  const plans = enrichPlans(overrides.plans || []);

  return {
    selected_plan_code: overrides.selected_plan_code ?? mockStore.selected_plan_code,
    platform_seat_limit: overrides.platform_seat_limit ?? mockStore.platform_seat_limit,
    platform_seats_used: overrides.platform_seats_used ?? mockStore.platform_seats_used,
    seat_limit_min: mockStore.seat_limit_min,
    seat_limit_max: mockStore.seat_limit_max,
    monthly_amount: overrides.monthly_amount ?? mockStore.monthly_amount,
    plans,
    platform_subscription_editable: true,
    ...overrides,
  };
}

function mergePlatformBilling(apiData = {}) {
  const remote = apiData.platform_subscription;
  if (!remote) {
    return USE_MOCK ? createMockPlatformBilling() : null;
  }

  const plans = enrichPlans(remote.plans || []);

  if (!USE_MOCK) {
    return {
      ...remote,
      plans,
      platform_subscription_editable: remote.platform_subscription_editable ?? true,
      seat_limit_editable: remote.seat_limit_editable ?? true,
    };
  }

  return createMockPlatformBilling({
    selected_plan_code: remote.selected_plan_code,
    platform_seat_limit: remote.platform_seat_limit,
    platform_seats_used: remote.platform_seats_used,
    monthly_amount: remote.monthly_amount,
    billing_setup_complete: remote.billing_setup_complete,
    seat_limit_max: remote.seat_limit_max,
    seat_limit_editable: remote.seat_limit_editable,
    agency_base_monthly: remote.agency_base_monthly,
    plans,
  });
}

function normalizeConfig(data = {}) {
  const plan = data.agency_billing_plan || {};

  return {
    billingMode: data.billing_mode || "agency_bill",
    billingModeChangeNotice:
      data.billing_mode_change_notice ||
      "Billing mode is set at signup. Contact support to switch between agency bill and producer bill.",
    creditsSharedPct: plan.credits_shared_with_producers_pct ?? 100,
    feeCoveragePct: plan.billing_covered_per_producer_pct ?? 100,
    platformFeeNotice:
      plan.billing_covered_per_producer_pct_notice ||
      "Platform fee coverage is set from your BizAssure billing plan. Contact support to change.",
    billingManagement: data.billing_management || {
      profile: "self_serve",
      notice: null,
      editable: true,
    },
    bizassureLink: data.bizassure_link || {
      agency_mapped: false,
      user_linked: false,
      requires_user_link: true,
      requires_agency_mapping: true,
    },
    creditsSharedEditable: plan.credits_shared_with_producers_pct_editable ?? true,
    platform: mergePlatformBilling(data),
    requiresCheckout: Boolean(data.requires_checkout),
    checkoutUrl: data.checkout_url || null,
    checkoutSessionId: data.checkout_session_id || null,
    message: data.message || null,
  };
}

export async function fetchAgencyBillingConfig() {
  const response = await axios.get(`${API_URL}/agency/billing`, {
    headers: authHeader(),
  });

  return normalizeConfig(response.data || {});
}

export async function saveAgencyBillingConfig(payload) {
  if (USE_MOCK && payload.platform) {
    Object.assign(mockStore, payload.platform);
  }

  const response = await axios.patch(
    `${API_URL}/agency/billing`,
    {
      credits_shared_with_producers_pct: payload.credits_shared_with_producers_pct,
      selected_plan_code: payload.platform?.selected_plan_code,
      platform_seat_limit: payload.platform?.platform_seat_limit,
      billing_interval: payload.platform?.billing_interval,
    },
    { headers: authHeader() }
  );

  const normalized = normalizeConfig(response.data || {});

  if (USE_MOCK && payload.platform) {
    syncMockMonthlyAmount(normalized.platform?.plans || []);
    normalized.platform = createMockPlatformBilling({
      ...(normalized.platform || {}),
      plans: normalized.platform?.plans || [],
    });
  }

  return normalized;
}

export async function completeAgencyBillingCheckout(sessionId) {
  const response = await axios.post(
    `${API_URL}/agency/billing/complete-checkout`,
    { session_id: sessionId },
    { headers: authHeader() }
  );

  return normalizeConfig(response.data || {});
}

export async function endAgencyBillingTrial() {
  const response = await axios.post(
    `${API_URL}/agency/billing/end-trial`,
    {},
    { headers: authHeader() }
  );

  return normalizeConfig(response.data || {});
}

export async function createAgencyBillingPortalSession() {
  const response = await axios.post(
    `${API_URL}/agency/billing/create-portal-session`,
    {},
    { headers: authHeader() }
  );

  const data = response.data || {};
  return { url: data.url || data?.data?.url || null };
}

export function planByCode(code, plans = []) {
  if (!code) {
    return null;
  }

  return plans.find((plan) => plan.code === code) || null;
}

export function estimatePlanMonthly(plan, options = {}) {
  return estimatePlanMonthlyEquivalent(plan, options);
}

export function estimatePlanTotal(plan, options = {}) {
  return estimateMonthlyTotal(plan, options);
}

export function isMockBillingEnabled() {
  return USE_MOCK;
}

// Backward-compatible export for any legacy imports.
export const PLATFORM_PLANS = PLATFORM_PLAN_META;
